import { useAgents } from '#~/agent/hooks.js';
import { fieldKeyedAgentsEqual, idNamePick } from '#~/agent/provider/use-agents-field-eq.js';
import { shallow } from 'zustand/shallow';
import { broker, ConnectionTimeout } from '#~/agent/index.js';
import { Provider as AgentProvider } from '#~/agent/provider/index.js';
import { toHexFromId } from '#~/api/utils.js';
import { useDepot } from '#~/depot/hooks.js';
import { NotFoundError } from '#~/error/index.js';
import { useLastRoomIdPref } from '#~/room/hooks.js';
import { Provider as RoomProvider } from '#~/room/provider/index.js';
import { Loader } from '@studio/design-system/loader';
import type { ChildrenOnly } from '@studio/design-system/props';
import { normalizeId } from '@studio/models';
import { parseQuery } from '@studio/models/net';
import type { Room as M_Room } from '@studio/models/room';
import { useLazyRef } from '@studio/react-utils/hooks';
import { toBoolean } from '@tryiris-ai/js-types';
import type { ReactElement } from 'react';
import { useEffect } from 'react';
import { JSONTree } from 'react-json-tree';
import { useParams } from 'react-router-dom';
import { Placeholder } from './placeholder.js';
import type { Props } from './types.js';

/** @internal `Inspector` component. */
const Inspector = ({
  roomId,
  roomName
}: Props & { roomId: M_Room['id']; roomName: M_Room['name'] }): ReactElement => {
  const agents = useAgents(
    (µ) => µ.map((a) => ({ id: a.id, name: a.name })),
    (a, b) => fieldKeyedAgentsEqual(a, b, idNamePick)
  );
  const data: Record<string, any> = {};

  for (const agent of agents) {
    const proxy = broker.acquire(agent.id);
    const key = `Agent: ${agent.name} (${agent.id})`;
    data[key] = {
      embedded: proxy?.embedded,
      state: proxy?.state
    };
  }

  return (
    <div className='flex h-full w-full flex-col overflow-hidden'>
      <div className='flex-shrink-0 p-4'>
        <h2 className='font-semibold mb-4'>
          Room: {roomName} ({roomId})
        </h2>
      </div>

      <div className='flex-grow overflow-auto px-4 pb-4'>
        {Object.keys(data).length === 0 && <p>No data</p>}
        {Object.keys(data).map((key) => {
          return (
            <div key={key} className='border-gray-200 rounded-md mb-6 border p-4'>
              <h3 className='mb-2 text-lg font-medium'>{key}</h3>
              <div className='max-h-[500px] overflow-auto'>
                <JSONTree
                  data={data[key]}
                  postprocessValue={(v) => {
                    // Check if it's a Uint8Array
                    if (v && v.constructor && v.constructor.name === 'Uint8Array') {
                      return { $type: 'bytes', value: toHexFromId(v as Uint8Array) };
                    }
                    return v;
                  }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

/** @internal `Inner` component. */
const Inner = ({ roomId, ...props }: Props & { roomId: M_Room['id'] }): ReactElement => {
  // Delay page load until room data is ready.
  const room = useDepot((µ) => µ.rooms[roomId]);

  return (
    <>
      {!!room ? (
        <Inspector roomId={room.id} roomName={room.name} {...props} />
      ) : (
        <Loader delay={{ delayMillis: ConnectionTimeout }} />
      )}
    </>
  );
};

/** Room guard. */
const Guard = ({ children, roomId }: ChildrenOnly & { roomId: M_Room['id'] }): ReactElement => {
  // If the agent is online, cool.  Otherwise show a placeholder.
  const statuses = useAgents((µ) => µ.map((a) => a.status), shallow);
  const allOffline = statuses.every((s) => s === 'offline');

  return allOffline ? (
    <>
      <Placeholder roomId={roomId} />
    </>
  ) : (
    <>{children}</>
  );
};

/** `<Room>` route. */
export const RoomInspect = (props: Props): ReactElement => {
  const params = useParams();

  const roomId = normalizeId<M_Room['id']>(params.roomId);

  const [_, setLastRoomId] = useLastRoomIdPref();

  useEffect(() => {
    setLastRoomId(roomId);
  }, [roomId]);

  if (!roomId) {
    throw new NotFoundError();
  }

  const options = useLazyRef(() => {
    // Parse query string.
    const params = parseQuery(window.location.search);

    return {
      restore: toBoolean(params['agent:restore'], { default: true })
    };
  }).current;

  return (
    <RoomProvider options={options} roomId={roomId}>
      <AgentProvider roomId={roomId}>
        <Guard roomId={roomId}>
          <Inner roomId={roomId} {...props} />
        </Guard>
      </AgentProvider>
    </RoomProvider>
  );
};
